import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type KeyboardEvent,
  type ReactElement,
} from 'react';
import { createPortal } from 'react-dom';
import { isSeparator, type ContextMenuEntry, type ContextMenuItem } from './types';

export interface ContextMenuViewProps {
  x: number;
  y: number;
  items: ContextMenuEntry[];
  onClose: () => void;
}

/** Estimated menu width used for edge-flip before first measure. */
const EST_WIDTH = 220;
/** Viewport padding kept around the menu when clamping. */
const MARGIN = 8;

/** Indices of the interactive (non-separator, non-disabled) items. */
function selectableIndices(items: ContextMenuEntry[]): number[] {
  const out: number[] = [];
  items.forEach((entry, i) => {
    if (!isSeparator(entry) && !entry.disabled) out.push(i);
  });
  return out;
}

/**
 * The rendered context menu: a portalled, absolutely-positioned popup. Flips at
 * the right/bottom viewport edges, traps focus, supports keyboard navigation
 * (Up/Down move, Home/End jump, Enter/Space activate, Esc/Left close, Tab
 * cycles), and dismisses on click-outside / scroll / blur / Esc / window resize.
 *
 * Rendered into `document.body` so it escapes overflow/transform ancestors —
 * fine under jsdom (which has a body) for tests.
 */
export function ContextMenuView({ x, y, items, onClose }: ContextMenuViewProps): ReactElement {
  const menuRef = useRef<HTMLDivElement | null>(null);
  const [pos, setPos] = useState<{ left: number; top: number }>({ left: x, top: y });
  // Index into `items` of the currently-active (focused) row.
  const [active, setActive] = useState<number>(() => selectableIndices(items)[0] ?? -1);
  // The element focused before we opened, restored on close.
  const restoreFocus = useRef<Element | null>(null);
  // Live Alt/Option-key state — swaps each item's altLabel / altOnSelect /
  // altIcon / altDanger in for the duration of the hold. Lets a single menu
  // entry safely expose a second, destructive alternate (e.g. Move to Trash
  // → Delete Permanently…) without an open menu item or keyboard shortcut.
  const [altHeld, setAltHeld] = useState(false);
  useEffect(() => {
    const onKey = (e: globalThis.KeyboardEvent): void => {
      setAltHeld(e.altKey);
    };
    window.addEventListener('keydown', onKey, true);
    window.addEventListener('keyup', onKey, true);
    return () => {
      window.removeEventListener('keydown', onKey, true);
      window.removeEventListener('keyup', onKey, true);
    };
  }, []);

  // Position with edge-flip. Measure after mount when we know the real size;
  // fall back to an estimate for the very first paint to avoid overflow flash.
  useLayoutEffect(() => {
    const vw = window.innerWidth || EST_WIDTH * 4;
    const vh = window.innerHeight || EST_WIDTH * 4;
    const el = menuRef.current;
    const w = el?.offsetWidth || EST_WIDTH;
    const h = el?.offsetHeight || items.length * 30 + 8;
    // Flip horizontally/vertically if the menu would overflow the edge.
    let left = x + w > vw - MARGIN ? x - w : x;
    let top = y + h > vh - MARGIN ? y - h : y;
    left = Math.max(MARGIN, Math.min(left, vw - w - MARGIN));
    top = Math.max(MARGIN, Math.min(top, vh - h - MARGIN));
    setPos({ left, top });
  }, [x, y, items.length]);

  // Focus management: remember the previously-focused element, focus the menu,
  // restore on unmount.
  useEffect(() => {
    restoreFocus.current = document.activeElement;
    menuRef.current?.focus();
    return () => {
      const el = restoreFocus.current;
      if (el instanceof HTMLElement && document.contains(el)) el.focus();
    };
  }, []);

  // Dismiss on outside interaction. Capture phase so it runs before re-opens.
  useEffect(() => {
    const onPointerDown = (e: Event): void => {
      if (!menuRef.current?.contains(e.target as Node)) onClose();
    };
    const onScrollOrBlur = (): void => onClose();
    document.addEventListener('pointerdown', onPointerDown, true);
    document.addEventListener('scroll', onScrollOrBlur, true);
    window.addEventListener('resize', onScrollOrBlur);
    window.addEventListener('blur', onScrollOrBlur);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown, true);
      document.removeEventListener('scroll', onScrollOrBlur, true);
      window.removeEventListener('resize', onScrollOrBlur);
      window.removeEventListener('blur', onScrollOrBlur);
    };
  }, [onClose]);

  const moveActive = useCallback(
    (dir: 1 | -1): void => {
      const sel = selectableIndices(items);
      if (sel.length === 0) return;
      const cur = sel.indexOf(active);
      const next = cur < 0 ? (dir === 1 ? 0 : sel.length - 1) : (cur + dir + sel.length) % sel.length;
      setActive(sel[next]);
    },
    [items, active],
  );

  const activate = useCallback(
    (index: number, alt: boolean): void => {
      const entry = items[index];
      if (!entry || isSeparator(entry) || entry.disabled) return;
      onClose();
      const handler = alt && entry.altOnSelect ? entry.altOnSelect : entry.onSelect;
      handler();
    },
    [items, onClose],
  );

  const onKeyDown = useCallback(
    (e: KeyboardEvent<HTMLDivElement>): void => {
      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          moveActive(1);
          break;
        case 'ArrowUp':
          e.preventDefault();
          moveActive(-1);
          break;
        case 'Home': {
          e.preventDefault();
          const first = selectableIndices(items)[0];
          if (first != null) setActive(first);
          break;
        }
        case 'End': {
          e.preventDefault();
          const sel = selectableIndices(items);
          const last = sel[sel.length - 1];
          if (last != null) setActive(last);
          break;
        }
        case 'Enter':
        case ' ':
          e.preventDefault();
          if (active >= 0) activate(active, e.altKey);
          break;
        case 'Escape':
        case 'ArrowLeft':
          e.preventDefault();
          onClose();
          break;
        case 'Tab':
          // Trap focus: cycle within the menu rather than escaping it.
          e.preventDefault();
          moveActive(e.shiftKey ? -1 : 1);
          break;
        default:
          break;
      }
    },
    [moveActive, activate, active, items, onClose],
  );

  const menu = (
    <div
      ref={menuRef}
      className="mp-ctxmenu"
      role="menu"
      tabIndex={-1}
      aria-orientation="vertical"
      style={{ left: pos.left, top: pos.top }}
      onKeyDown={onKeyDown}
      onContextMenu={(e) => e.preventDefault()}
    >
      {items.map((entry, index) => {
        if (isSeparator(entry)) {
          return <div key={entry.id} className="mp-ctxmenu__sep" role="separator" />;
        }
        const item: ContextMenuItem = entry;
        // Swap to the alt branch only when the entry actually defined one and
        // Alt is held — keeps every other entry stable while Alt is pressed.
        const showAlt = altHeld && item.altOnSelect != null;
        const Icon = (showAlt ? item.altIcon : undefined) ?? item.icon;
        const label = showAlt && item.altLabel ? item.altLabel : item.label;
        const danger = showAlt ? (item.altDanger ?? item.danger) : item.danger;
        const isActive = index === active;
        return (
          <button
            key={item.id}
            type="button"
            role="menuitem"
            data-ctx-item={item.id}
            className="mp-ctxmenu__item"
            data-active={isActive ? '' : undefined}
            data-danger={danger ? '' : undefined}
            disabled={item.disabled}
            aria-disabled={item.disabled || undefined}
            tabIndex={-1}
            onMouseEnter={() => !item.disabled && setActive(index)}
            onClick={(e) => activate(index, e.altKey)}
          >
            <span className="mp-ctxmenu__icon" aria-hidden>
              {Icon ? <Icon size={14} aria-hidden /> : null}
            </span>
            <span className="mp-ctxmenu__label">{label}</span>
            {item.shortcut && !showAlt && (
              <span className="mp-ctxmenu__shortcut">{item.shortcut}</span>
            )}
          </button>
        );
      })}
    </div>
  );

  return createPortal(menu, document.body);
}
